/**
 * ALAYA JUNIOR - Contact Widgets
 * Professional contact widget functionality
 *
 * Features:
 * - Floating multi-channel contact widget
 * - Product page photo request system
 * - Rate limiting with localStorage
 * - Working hours awareness (PKT timezone)
 * - Accessibility support
 */

(function() {
  'use strict';

  // =============================================================================
  // Configuration
  // =============================================================================

  const CONFIG = {
    whatsapp: {
      number: '923235548508',
      defaultMessage: 'Hi Alaya Junior, I have a question about your products.'
    },
    workingHours: {
      start: 9,  // 9 AM PKT
      end: 21,   // 9 PM PKT
      timezone: 'Asia/Karachi'
    },
    photoRequest: {
      delayMs: 5000,            // 5 seconds delay
      storageKey: 'aj_photo_requests',
      expiryDays: 7
    }
  };

  // =============================================================================
  // Utility Functions
  // =============================================================================

  /**
   * Check if current time is within working hours (PKT timezone)
   */
  function isWithinWorkingHours() {
    try {
      const now = new Date();
      const pktTime = new Date(now.toLocaleString('en-US', { timeZone: CONFIG.workingHours.timezone }));
      const hour = pktTime.getHours();
      return hour >= CONFIG.workingHours.start && hour < CONFIG.workingHours.end;
    } catch (e) {
      // Fallback if timezone is not supported
      const hour = new Date().getHours();
      return hour >= CONFIG.workingHours.start && hour < CONFIG.workingHours.end;
    }
  }

  /**
   * Get stored photo requests from localStorage
   */
  function getStoredRequests() {
    try {
      const stored = localStorage.getItem(CONFIG.photoRequest.storageKey);
      if (!stored) return {};

      const data = JSON.parse(stored);
      const now = Date.now();
      const expiryMs = CONFIG.photoRequest.expiryDays * 24 * 60 * 60 * 1000;

      // Clean up expired entries
      const cleaned = {};
      for (const [productId, timestamp] of Object.entries(data)) {
        if (now - timestamp < expiryMs) {
          cleaned[productId] = timestamp;
        }
      }

      // Save cleaned data back
      if (Object.keys(cleaned).length !== Object.keys(data).length) {
        localStorage.setItem(CONFIG.photoRequest.storageKey, JSON.stringify(cleaned));
      }

      return cleaned;
    } catch (e) {
      return {};
    }
  }

  /**
   * Check if a photo request was already made for a product
   */
  function hasRequestedPhotos(productId) {
    const requests = getStoredRequests();
    return productId in requests;
  }

  /**
   * Mark a product as having requested photos
   */
  function markPhotoRequested(productId) {
    try {
      const requests = getStoredRequests();
      requests[productId] = Date.now();
      localStorage.setItem(CONFIG.photoRequest.storageKey, JSON.stringify(requests));
    } catch (e) {
      // localStorage may be unavailable
    }
  }

  /**
   * Generate WhatsApp URL with pre-filled message
   */
  function generateWhatsAppUrl(message) {
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${CONFIG.whatsapp.number}?text=${encodedMessage}`;
  }

  /**
   * Format price for display (handles Shopify money format)
   */
  function formatPrice(priceInCents) {
    const price = priceInCents / 100;
    return 'Rs. ' + price.toLocaleString('en-PK');
  }

  // =============================================================================
  // Floating Contact Widget
  // =============================================================================

  class FloatingContactWidget {
    constructor(container) {
      this.container = container;
      this.trigger = container.querySelector('.aj-contact-trigger');
      this.menu = container.querySelector('.aj-contact-menu');
      this.isOpen = false;

      this.init();
    }

    init() {
      // Toggle on trigger click
      this.trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });

      // Close when clicking outside
      document.addEventListener('click', (e) => {
        if (this.isOpen && !this.container.contains(e.target)) {
          this.close();
        }
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
          this.trigger.focus();
        }
      });

      // Handle keyboard navigation within menu
      this.menu.addEventListener('keydown', (e) => {
        this.handleMenuKeydown(e);
      });
    }

    toggle() {
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.isOpen = true;
      this.container.classList.add('is-open');
      this.trigger.setAttribute('aria-expanded', 'true');

      // Focus first menu item
      const firstLink = this.menu.querySelector('.aj-contact-menu__link');
      if (firstLink) {
        setTimeout(() => firstLink.focus(), 100);
      }
    }

    close() {
      this.isOpen = false;
      this.container.classList.remove('is-open');
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    handleMenuKeydown(e) {
      const links = Array.from(this.menu.querySelectorAll('.aj-contact-menu__link'));
      const currentIndex = links.indexOf(document.activeElement);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          const nextIndex = (currentIndex + 1) % links.length;
          links[nextIndex].focus();
          break;
        case 'ArrowUp':
          e.preventDefault();
          const prevIndex = currentIndex <= 0 ? links.length - 1 : currentIndex - 1;
          links[prevIndex].focus();
          break;
        case 'Home':
          e.preventDefault();
          links[0].focus();
          break;
        case 'End':
          e.preventDefault();
          links[links.length - 1].focus();
          break;
      }
    }
  }

  // =============================================================================
  // Product Photo Request Widget
  // =============================================================================

  class PhotoRequestWidget {
    constructor(container, productData) {
      this.container = container;
      this.productData = productData;
      this.button = container.querySelector('.aj-photo-request__button');
      this.infoEl = container.querySelector('.aj-photo-request__info');
      this.hoursEl = container.querySelector('.aj-photo-request__hours');
      this.modal = null;
      this.selectedVerifications = [];

      this.init();
    }

    init() {
      // Check if already requested
      if (hasRequestedPhotos(this.productData.id)) {
        this.showRequestSentState();
        this.container.style.display = 'block';
        return;
      }

      // Update working hours indicator
      this.updateHoursIndicator();

      // Show button after delay
      setTimeout(() => {
        this.container.style.display = 'block';
        this.button.disabled = false;
      }, CONFIG.photoRequest.delayMs);

      // Button click handler
      this.button.addEventListener('click', () => {
        this.openModal();
      });
    }

    updateHoursIndicator() {
      if (!this.hoursEl) return;

      const dot = this.hoursEl.querySelector('.aj-photo-request__hours-dot');
      const text = this.hoursEl.querySelector('span:last-child');

      if (isWithinWorkingHours()) {
        dot.classList.add('aj-photo-request__hours-dot--open');
        dot.classList.remove('aj-photo-request__hours-dot--closed');
        text.textContent = 'Available now (9 AM - 9 PM PKT)';
      } else {
        dot.classList.add('aj-photo-request__hours-dot--closed');
        dot.classList.remove('aj-photo-request__hours-dot--open');
        text.textContent = 'Away - Replies within business hours (9 AM - 9 PM PKT)';
      }
    }

    showRequestSentState() {
      this.button.disabled = true;
      this.button.innerHTML = `
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
        </svg>
        Photo Request Sent
      `;

      if (this.infoEl) {
        this.infoEl.classList.add('aj-photo-request__info--success');
        this.infoEl.innerHTML = `
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
          </svg>
          <span>Your request was sent. We typically respond within 10-30 minutes during business hours.</span>
        `;
      }
    }

    openModal() {
      this.createModal();
      document.body.appendChild(this.modal);

      // Trigger reflow for animation
      this.modal.offsetHeight;
      this.modal.classList.add('is-visible');

      // Prevent body scroll
      document.body.style.overflow = 'hidden';

      // Focus first checkbox
      const firstCheckbox = this.modal.querySelector('.aj-modal__checkbox');
      if (firstCheckbox) {
        setTimeout(() => firstCheckbox.focus(), 100);
      }
    }

    closeModal() {
      if (!this.modal) return;

      this.modal.classList.remove('is-visible');
      document.body.style.overflow = '';

      setTimeout(() => {
        if (this.modal && this.modal.parentNode) {
          this.modal.parentNode.removeChild(this.modal);
        }
        this.modal = null;
      }, 300);

      // Return focus to button
      this.button.focus();
    }

    createModal() {
      const overlay = document.createElement('div');
      overlay.className = 'aj-modal-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-labelledby', 'aj-modal-title');

      const verificationOptions = [
        { id: 'color', label: 'Color Accuracy', desc: 'Verify exact color matches your expectations' },
        { id: 'size', label: 'Size & Measurements', desc: 'Confirm dimensions and fit details' },
        { id: 'material', label: 'Material Quality', desc: 'Check fabric quality and texture' },
        { id: 'condition', label: 'Overall Condition', desc: 'Assess product condition and finishing' },
        { id: 'packaging', label: 'Packaging', desc: 'View how the item will be packaged' }
      ];

      const checkboxesHtml = verificationOptions.map(opt => `
        <label class="aj-modal__checkbox" data-verification="${opt.id}" tabindex="0">
          <span class="aj-modal__checkbox-input">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
            </svg>
          </span>
          <span class="aj-modal__checkbox-content">
            <span class="aj-modal__checkbox-label">${opt.label}</span>
            <span class="aj-modal__checkbox-desc">${opt.desc}</span>
          </span>
        </label>
      `).join('');

      overlay.innerHTML = `
        <div class="aj-modal">
          <div class="aj-modal__header">
            <div>
              <h2 class="aj-modal__title" id="aj-modal-title">Request Real Stock Photos</h2>
              <p class="aj-modal__subtitle">Select what you would like us to verify</p>
            </div>
            <button class="aj-modal__close" type="button" aria-label="Close dialog">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"/>
              </svg>
            </button>
          </div>
          <div class="aj-modal__body">
            <div class="aj-modal__product">
              <img class="aj-modal__product-image" src="${this.productData.image}" alt="${this.productData.title}">
              <div class="aj-modal__product-info">
                <h3 class="aj-modal__product-title">${this.productData.title}</h3>
                <div class="aj-modal__product-price">${this.productData.price}</div>
              </div>
            </div>
            <p class="aj-modal__section-title">What would you like to verify?</p>
            <div class="aj-modal__checkboxes">
              ${checkboxesHtml}
            </div>
            <div class="aj-modal__error">Please select at least one item to verify</div>
          </div>
          <div class="aj-modal__footer">
            <button class="aj-modal__submit" type="button">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Send Request via WhatsApp
            </button>
            <p class="aj-modal__note">One request per product per session</p>
          </div>
        </div>
      `;

      this.modal = overlay;
      this.selectedVerifications = [];

      // Event listeners
      const closeBtn = overlay.querySelector('.aj-modal__close');
      closeBtn.addEventListener('click', () => this.closeModal());

      // Close on overlay click
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.closeModal();
        }
      });

      // Close on escape
      overlay.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeModal();
        }
      });

      // Checkbox interactions
      const checkboxes = overlay.querySelectorAll('.aj-modal__checkbox');
      checkboxes.forEach(checkbox => {
        const toggle = () => {
          const verificationId = checkbox.dataset.verification;
          const isChecked = checkbox.classList.toggle('is-checked');

          if (isChecked) {
            this.selectedVerifications.push(verificationId);
          } else {
            this.selectedVerifications = this.selectedVerifications.filter(v => v !== verificationId);
          }

          // Hide error when something is selected
          const error = overlay.querySelector('.aj-modal__error');
          if (this.selectedVerifications.length > 0) {
            error.classList.remove('is-visible');
          }
        };

        checkbox.addEventListener('click', toggle);
        checkbox.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        });
      });

      // Submit handler
      const submitBtn = overlay.querySelector('.aj-modal__submit');
      submitBtn.addEventListener('click', () => this.submitRequest());
    }

    submitRequest() {
      // Validate at least one selection
      if (this.selectedVerifications.length === 0) {
        const error = this.modal.querySelector('.aj-modal__error');
        error.classList.add('is-visible');
        return;
      }

      // Build verification labels
      const verificationLabels = {
        color: 'Color accuracy verification',
        size: 'Size/measurements confirmation',
        material: 'Material quality check',
        condition: 'Overall condition assessment',
        packaging: 'Packaging verification'
      };

      const selectedLabels = this.selectedVerifications
        .map(v => '- ' + verificationLabels[v])
        .join('\n');

      // Generate message
      const message = `Hi Alaya Junior,

I'm interested in ${this.productData.title} (${this.productData.price})

I would like to verify the following before purchase:
${selectedLabels}

Product Link: ${this.productData.url}

Could you please send real photos from your current stock?`;

      // Mark as requested
      markPhotoRequested(this.productData.id);

      // Close modal
      this.closeModal();

      // Update button state
      this.showRequestSentState();

      // Open WhatsApp
      const whatsappUrl = generateWhatsAppUrl(message);
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    }
  }

  // =============================================================================
  // Initialize Widgets
  // =============================================================================

  function initFloatingWidget() {
    const container = document.querySelector('.aj-contact-widget');
    if (container) {
      new FloatingContactWidget(container);
    }
  }

  function initPhotoRequestWidget() {
    const container = document.querySelector('.aj-photo-request');
    if (!container) return;

    // Get product data from data attributes
    const productData = {
      id: container.dataset.productId,
      title: container.dataset.productTitle,
      price: container.dataset.productPrice,
      url: container.dataset.productUrl,
      image: container.dataset.productImage
    };

    if (productData.id) {
      new PhotoRequestWidget(container, productData);
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initFloatingWidget();
      initPhotoRequestWidget();
    });
  } else {
    initFloatingWidget();
    initPhotoRequestWidget();
  }

  // Expose for external use if needed
  window.AJContactWidgets = {
    FloatingContactWidget,
    PhotoRequestWidget,
    isWithinWorkingHours,
    generateWhatsAppUrl
  };

})();
